import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useTranslation } from 'react-i18next';
import { CartContent } from './CartContent';
import { CartCheckout } from './CartCheckout';
import { getCart } from '@/utils/cart';
import { ShoppingCart } from '@/types';

interface CartModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const CartModal: React.FC<CartModalProps> = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const [cart, setCart] = useState<ShoppingCart>({ items: [], total: 0 });
  const [scale, setScale] = useState(1);
  const [isPinching, setIsPinching] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen) {
      setCart(getCart());
      // Prevent background scrolling when modal is open
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
    } else {
      // Restore background scrolling when modal is closed
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
    }
    
    return () => {
      // Cleanup on unmount
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
    };
  }, [isOpen]);

  useEffect(() => {
    const updateCartData = () => {
      if (isOpen) {
        setCart(getCart());
      }
    };

    window.addEventListener('cartUpdated', updateCartData);
    return () => window.removeEventListener('cartUpdated', updateCartData);
  }, [isOpen]);

  // Simple touch handlers for pinch zoom only
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    if (e.touches.length === 2) {
      setIsPinching(true);
      const touch1 = e.touches[0];
      const touch2 = e.touches[1];
      const distance = Math.hypot(touch1.clientX - touch2.clientX, touch1.clientY - touch2.clientY);
      modalRef.current?.setAttribute('data-initial-distance', distance.toString());
    }
  }, []);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (e.touches.length === 2 && isPinching) {
      e.preventDefault();
      const touch1 = e.touches[0];
      const touch2 = e.touches[1];
      const distance = Math.hypot(touch1.clientX - touch2.clientX, touch1.clientY - touch2.clientY);
      const initialDistance = parseFloat(modalRef.current?.getAttribute('data-initial-distance') || '0');
      
      if (initialDistance > 0) {
        const newScale = Math.max(0.8, Math.min(2, scale * (distance / initialDistance)));
        setScale(newScale);
      }
    }
  }, [isPinching, scale]);

  const handleTouchEnd = useCallback(() => {
    setIsPinching(false);
  }, []);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-[2500] flex items-end md:items-center justify-center p-0 md:p-4">
      <div 
        ref={modalRef}
        className="bg-background w-full h-full md:h-[85vh] md:max-w-4xl md:max-h-[90vh] md:rounded-lg overflow-hidden relative flex flex-col"
        style={{
          transform: `scale(${scale})`,
          transformOrigin: 'center center'
        }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onWheel={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-background border-b p-4 flex items-center justify-between z-10 flex-shrink-0">
          <h2 className="text-xl font-semibold">{t('cart.title')} ({cart.items.length})</h2>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={onClose}>
              <X className="w-5 h-5" />
            </Button>
          </div>
        </div>

        {/* Scrollable cart content */}
        <div 
          ref={contentRef}
          className="flex-1 overflow-y-auto p-4 pb-0"
          style={{ 
            WebkitOverflowScrolling: 'touch',
            overscrollBehavior: 'contain'
          }}
        >
          <CartContent 
            showClearButton={true}
            showHeader={false}
            showCheckout={false}
            onClearCart={() => {
              setCart({ items: [], total: 0 });
            }}
          />
        </div>

        {/* Fixed checkout section at bottom */}
        <CartCheckout className="flex-shrink-0" />
      </div>
    </div>
  );
};